"use client";

import { useEffect } from "react";
import Modal from "react-modal";
import { useGetTripDetails } from "@/hooks/useCommon";
import { X, MapPinned } from "lucide-react";

interface TripDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinearyId: string;
  isCommingFromItineary?: boolean;
}

const TripDetailModal = ({
  isOpen,
  onClose,
  itinearyId,
  isCommingFromItineary = false,
}: TripDetailModalProps) => {
  const {
    data: tripDetails,
    error,
    isLoading,
  } = useGetTripDetails(itinearyId || "");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nextApp = document.getElementById("__next");
      if (nextApp) {
        Modal.setAppElement(nextApp);
      }
    }
  }, []);

  const tripDet = tripDetails?.results ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 bg-black/20  flex items-center justify-center z-50"
      className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-5xl mx-4 outline-none"
      contentLabel="Trip Details Modal"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPinned className="text-blue-600" size={20} />
            <h2 className="text-sm font-semibold text-gray-800">
              Trip Details –{" "}
              <span className="text-sm text-blue-600">#{itinearyId}</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="text-center text-xs text-gray-500">Loading...</div>
        ) : error ? (
          <div className="text-center text-xs text-red-500">
            Error loading trip details.
          </div>
        ) : Array.isArray(tripDet) && tripDet.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-[#55bd42] text-[11px] font-semibold text-white text-left">
                <tr>
                  <th className="p-2">Days</th>
                  <th className="p-2">Pickup City</th>
                  <th className="p-2">Drop City</th>
                  <th className="p-2">Pickup Date</th>
                  <th className="p-2">Pickup Time</th>
                  <th className="p-2">Drop Date</th>
                  <th className="p-2">Drop Time</th>
                  <th className="p-2">Distance</th>
                  <th className="p-2">Duration</th>
                  {isCommingFromItineary && (
                    <>
                      <th className="p-2">Pickup Address</th>
                      <th className="p-2">Drop Address</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tripDet.map((trip: any, index: number) => (
                  <tr key={index} className="border-t hover:bg-gray-50">
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2">{trip.pick_city_name || "N/A"}</td>
                    <td className="p-2">{trip.drop_city_name || "N/A"}</td>
                    <td className="p-2">{trip.pickup_date || "N/A"}</td>
                    <td className="p-2">{trip.pickup_time || "N/A"}</td>
                    <td className="p-2">{trip.drop_date || "N/A"}</td>
                    <td className="p-2">{trip.drop_time || "N/A"}</td>
                    <td className="p-2">
                      {trip.distance ? `${trip.distance} km` : "N/A"}
                    </td>
                    <td className="p-2">{trip.duration || "N/A"}</td>
                    {isCommingFromItineary && (
                      <>
                        <td className="p-2">{trip.pickup_address || "N/A"}</td>
                        <td className="p-2">{trip.drop_address || "N/A"}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-xs text-gray-500">
            No trip details found.
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] bg-gray-200 hover:bg-gray-300 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TripDetailModal;
